import { useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import PlayCircleOutlineOutlined from '@mui/icons-material/PlayCircleOutlineOutlined';
import { usePageEntityContext } from '@/lib/pageEntityContext';
import { useEntityActions } from '@/hooks/useEntityActions';
import type { SlashCommand } from '@/components/GlobalSearch/commands';

/**
 * Slash commands for whatever record the current page is showing.
 *
 * Nothing here decides *whether* an action is possible — the server already did that
 * when it answered `available-actions`, evaluating the state machine against this
 * specific record and returning `enabled`, `reason` and `missingFields` per action.
 * This hook only turns that answer into palette rows.
 *
 * Actions the server reports as not currently possible are dropped. The palette is a
 * do-this surface, not a status readout — the detail view's actions menu is where the
 * full set lives, greyed out with the reason.
 */
export function useContextCommands(): SlashCommand[] {
  const { module, entityType, entityId, displayField } = usePageEntityContext();
  const queryClient = useQueryClient();
  const { workflows, commands, triggerWorkflow, executeCommand } = useEntityActions({
    module,
    entity: entityType,
    entityId,
  });

  /**
   * `useMutation` hands back a fresh object every render. Depending on those objects
   * below would give this list a new identity each render, which cascades through the
   * command registry into `slash.flat` — and the dialog resets the selected index
   * whenever `slash.flat` changes. The visible symptom is that arrow keys and Tab stop
   * working: the selection moves, then snaps back to 0 on the next render. A ref keeps
   * the handlers reachable without putting them in the dependency array.
   */
  const mutations = useRef({ triggerWorkflow, executeCommand });
  mutations.current = { triggerWorkflow, executeCommand };

  return useMemo(() => {
    if (!module || !entityType || !entityId) return [];

    /**
     * The record's business identity ("ORD-001002"), so an action reads as acting on a
     * specific object rather than on "an order somewhere". Read from the cache the detail
     * view already filled — the palette should not fetch a record just to title a row, and
     * an unlabelled action is a fine outcome if the cache misses.
     *
     * Matched by shape rather than an exact key: the generated factories produce
     * `[plural, tenantId, 'detail', id]` and the plural is not derivable (`NumberSeries`
     * does not pluralise to `numberseriess`). The id being last is guaranteed by the
     * factory, which is enough to identify the entry.
     *
     * Read inside this memo deliberately: it recomputes when `commands`/`workflows` land,
     * which is the same page load that fills the record — so a late cache write still gets
     * picked up without this becoming a subscription.
     */
    const recordLabel = (() => {
      if (!displayField) return undefined;
      const entries = queryClient.getQueriesData<Record<string, unknown>>({
        predicate: (q) =>
          q.queryKey.includes('detail') && q.queryKey[q.queryKey.length - 1] === entityId,
      });
      for (const [, data] of entries) {
        const value = data?.[displayField];
        if (typeof value === 'string' && value) return value;
      }
      return undefined;
    })();

    const withIdentity = (label: string) => (recordLabel ? `${label} ${recordLabel}` : label);
    const built: SlashCommand[] = [];

    /**
     * Backend field naming is inverted from what it looks like: the POST path segment
     * is the raw `id`, while `name` holds the human label. The generated hooks swap
     * them into `{ name: cmd.id, displayName: cmd.name }` before use, so this mapping
     * has to keep the same pairing or every command would post to the wrong URL.
     */
    for (const command of commands) {
      if (!command.enabled) continue;
      built.push({
        id: `action.cmd.${entityType}.${command.id}`,
        title: { value: withIdentity(command.name || command.id) },
        subtitle: { value: entityType },
        group: 'actions',
        icon: BoltOutlined,
        permission: command.permission,
        run: (ctx) => {
          mutations.current.executeCommand.mutate(
            { commandName: command.id },
            {
              onSuccess: () => ctx.closeDialog(),
              onError: (error) => ctx.reportError?.(error),
            },
          );
        },
      });
    }

    for (const workflow of workflows) {
      for (const action of workflow.actions) {
        if (!action.enabled) continue;
        built.push({
          id: `action.wf.${entityType}.${workflow.workflowName}.${action.stepName}`,
          title: { value: withIdentity(action.name) },
          subtitle: { value: workflow.workflowName },
          group: 'actions',
          icon: PlayCircleOutlineOutlined,
          permission: action.permission,
          run: (ctx) => {
            mutations.current.triggerWorkflow.mutate(
              { workflowName: workflow.workflowName, stepName: action.stepName },
              {
                onSuccess: () => ctx.closeDialog(),
                onError: (error) => ctx.reportError?.(error),
              },
            );
          },
        });
      }
    }

    return built;
  }, [module, entityType, entityId, displayField, commands, workflows, queryClient]);
}
